import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { ConfigFormFooter } from './ConfigFormFooter';
import { DealStageRow } from './DealStageRow';
import { DealStagesHead } from './DealStagesHead';
import {
  canSaveStages,
  canToggleKind,
  canToggleRequiresAct,
  isStageRemovedError,
  stageKinds,
} from '../_lib/dealStagesEdit';
import type { RawRecord } from '../_lib/rawRecord';
import { useStagesDraft } from '../_lib/useStagesDraft';

/**
 * AUT-542 — `deals.stages` form-table: label, kinds, order (up / down
 * swaps two `order` values), «Скрыт». Edits a copy of the stored records
 * and saves it whole (RULINGS 11). No delete — a step is retired, never
 * removed.
 * AUT-633 — «Шаги заказа»: kind chips from `DEAL_KINDS`, «Нужны
 * фото» (`requiresAct`); adding a step is `addStage` («Добавить шаг»).
 * The draft and its guards live in `useStagesDraft`.
 */

const textOf = (v: unknown): string => (typeof v === 'string' ? v : '');

export interface DealStagesFormProps {
  initial: RawRecord[];
  busy: boolean;
  error?: string | undefined;
  onSave: (raw: RawRecord[]) => void;
  onCancel: () => void;
  onEditJson: () => void;
}

export const DealStagesForm = ({
  initial,
  busy,
  error,
  onSave,
  onCancel,
  onEditJson,
}: DealStagesFormProps): ReactElement => {
  const { t } = useTranslation();
  const stages = useStagesDraft(initial);
  const { draft, sorted } = stages;

  return (
    <Box data-testid="config-stages-form" sx={{ py: 1.5 }}>
      <Table size="small">
        <DealStagesHead />
        <TableBody>
          {sorted.map((idx, pos) => {
            const entry = draft[idx] ?? {};
            const key = textOf(entry.key);
            const isLast = pos === sorted.length - 1;
            return (
              <DealStageRow
                key={key || idx}
                label={textOf(entry.label)}
                kinds={stageKinds(entry)}
                requiresAct={entry.requiresAct === true}
                retired={entry.retired === true}
                isFirst={pos === 0}
                isLast={isLast}
                busy={busy}
                // eslint-disable-next-line jsx-a11y/no-autofocus -- the step the admin just added
                autoFocus={stages.focusLast && isLast}
                canToggleKind={(kind) => canToggleKind(initial, entry, kind)}
                canToggleRequiresAct={canToggleRequiresAct(entry)}
                onLabel={(label) => stages.edit(key, { label })}
                onToggleKind={(kind) => stages.toggleKind(entry, kind)}
                onMove={(by) => stages.move(pos, by)}
                onRequiresAct={(on) => stages.setRequiresAct(entry, on)}
                onRetired={(retired) => stages.edit(key, { retired })}
              />
            );
          })}
        </TableBody>
      </Table>
      <Box sx={{ pt: 1.5 }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          onClick={stages.add}
          disabled={busy}
          data-testid="sm-config-add-step"
        >
          {t('config.stages.addStep')}
        </Button>
      </Box>
      <ConfigFormFooter
        canSave={canSaveStages(draft)}
        busy={busy}
        error={isStageRemovedError(error) ? t('config.stages.staleList') : error}
        onSave={() => onSave(draft)}
        onCancel={onCancel}
        start={
          <Button
            size="small"
            startIcon={<EditIcon sx={{ fontSize: 14 }} />}
            onClick={onEditJson}
            disabled={busy}
            data-testid="sm-config-json"
          >
            {t('config.stages.editJson')}
          </Button>
        }
      />
    </Box>
  );
};
